import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Building2, Plus, Search } from 'lucide-react';
import { api } from '../../api';
import { pode, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { NOME_STATUS, NOME_TIPO } from '../../componentes/formatos';
import { FormCliente } from './FormCliente';

interface Item { id: string; tipo: string; razao_social: string; nome_fantasia: string | null; status: string; documento: string | null; locais: number; contatos: number; origem?: string }
interface Resposta { total: number; pagina: number; por_pagina: number; itens: Item[] }

export function ListaClientes() {
  const { usuario } = useSessao();
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  const [busca, setBusca] = useState(params.get('busca') ?? '');
  const [dados, setDados] = useState<Resposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const status = params.get('status') ?? '';
  const pagina = Number(params.get('pagina') ?? 1);
  const podeEditar = pode(usuario, 'cadastro.editar');

  useEffect(() => {
    const q = new URLSearchParams();
    if (params.get('busca')) q.set('busca', params.get('busca')!);
    if (status) q.set('status', status);
    if (pagina > 1) q.set('pagina', String(pagina));
    setErro(null);
    api<Resposta>(`/clientes?${q}`).then(setDados).catch((e) => setErro(e.message));
  }, [params, status, pagina]);

  // busca com atraso curto enquanto digita
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get('busca') ?? '') === busca) return;
      const p = new URLSearchParams(params);
      busca ? p.set('busca', busca) : p.delete('busca');
      p.delete('pagina');
      setParams(p, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [busca]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtro = (s: string) => { const p = new URLSearchParams(params); s ? p.set('status', s) : p.delete('status'); p.delete('pagina'); setParams(p); };
  const totalPaginas = dados ? Math.max(1, Math.ceil(dados.total / dados.por_pagina)) : 1;

  return (
    <>
      <div className="cabecalho-pagina">
        <div>
          <span className="kicker">Cadastro</span>
          <h1 className="titulo">Clientes</h1>
        </div>
        {podeEditar && <button className="btn" onClick={() => setNovo(true)}><Plus size={18} strokeWidth={2} aria-hidden /> Novo cliente</button>}
      </div>

      <div className="barra-filtros">
        <label className="campo-busca">
          <Search size={18} strokeWidth={1.7} aria-hidden />
          <input type="search" placeholder="Nome, nome fantasia ou CPF/CNPJ completo" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar clientes" />
        </label>
        <div className="segmentos" role="group" aria-label="Situação">
          {[['', 'Todos'], ['ativo', 'Ativos'], ['prospecto', 'Prospectos'], ['inativo', 'Inativos']].map(([v, r]) => (
            <button key={v} className={status === v ? 'ativo' : ''} onClick={() => filtro(v!)}>{r}</button>
          ))}
        </div>
      </div>

      {erro && <p className="alerta" role="alert">{erro}</p>}

      {dados && dados.itens.length === 0 && (
        <div className="cartao vazio">
          <div className="icone-tile"><Building2 size={24} strokeWidth={1.7} aria-hidden /></div>
          <p>{params.get('busca') || status ? 'Nenhum cliente encontrado com esse filtro.' : 'Nenhum cliente cadastrado ainda.'}</p>
          {podeEditar && !params.get('busca') && !status && <button className="btn" onClick={() => setNovo(true)}><Plus size={18} aria-hidden /> Cadastrar o primeiro</button>}
        </div>
      )}

      {dados && dados.itens.length > 0 && (
        <div className="cartao tabela-cartao">
          <table className="tabela tabela-clientes">
            <thead><tr><th>Cliente</th><th>Tipo</th><th>CPF/CNPJ</th><th className="num">Parques</th><th className="num">Contatos</th><th>Situação</th></tr></thead>
            <tbody>
              {dados.itens.map((c) => (
                <tr key={c.id} onClick={() => navegar(`/clientes/${c.id}`)}>
                  <td><Link to={`/clientes/${c.id}`} onClick={(e) => e.stopPropagation()}><strong>{c.razao_social}</strong></Link>{c.nome_fantasia && <small>{c.nome_fantasia}</small>}{c.origem === 'conta_azul' && <small className="origem-erp">Conta Azul</small>}</td>
                  <td>{NOME_TIPO[c.tipo]}</td>
                  <td className="mono">{c.documento ?? '—'}</td>
                  <td className="num">{c.locais}</td>
                  <td className="num">{c.contatos}</td>
                  <td><span className={`status status-${c.status}`}>{NOME_STATUS[c.status]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <footer className="rodape-tabela">
            <span>{dados.total} cliente{dados.total === 1 ? '' : 's'}</span>
            {totalPaginas > 1 && (
              <span className="paginacao">
                <button className="btn-fantasma" disabled={pagina <= 1} onClick={() => { const p = new URLSearchParams(params); p.set('pagina', String(pagina - 1)); setParams(p); }}>Anterior</button>
                {pagina} de {totalPaginas}
                <button className="btn-fantasma" disabled={pagina >= totalPaginas} onClick={() => { const p = new URLSearchParams(params); p.set('pagina', String(pagina + 1)); setParams(p); }}>Próxima</button>
              </span>
            )}
          </footer>
        </div>
      )}

      <Modal titulo="Novo cliente" aberto={novo} aoFechar={() => setNovo(false)}>
        <FormCliente aoSalvar={(id) => { setNovo(false); navegar(`/clientes/${id}`); }} aoCancelar={() => setNovo(false)} />
      </Modal>
    </>
  );
}
